import { format, parseISO, isValid } from "date-fns";

/**
 * Converts various date formats (Firestore Timestamp, ISO string, Date object) to JavaScript Date
 * @param {any} dateValue - The date value to convert
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const toDate = (dateValue) => {
  if (!dateValue) return null;

  // If it's already a Date object
  if (dateValue instanceof Date) {
    return isValid(dateValue) ? dateValue : null;
  }

  // If it's a Firestore Timestamp object (has seconds and nanoseconds)
  // Handle both server-side format (seconds/nanoseconds) and client-side format (_seconds/_nanoseconds)
  if (dateValue && typeof dateValue === "object" && !Array.isArray(dateValue)) {
    const hasSeconds = "seconds" in dateValue || "_seconds" in dateValue;
    const hasNanoseconds = "nanoseconds" in dateValue || "_nanoseconds" in dateValue;
    
    if (hasSeconds && hasNanoseconds) {
      try {
        // Extract seconds and nanoseconds, handling both formats
        const seconds = dateValue.seconds || dateValue._seconds;
        const nanoseconds = dateValue.nanoseconds || dateValue._nanoseconds;
        // Convert Firestore Timestamp to JavaScript Date
        const date = new Date(
          seconds * 1000 + nanoseconds / 1000000
        );
        return isValid(date) ? date : null;
      } catch (error) {
        console.warn("Failed to convert Firestore timestamp:", error);
        return null;
      }
    }
  }

  // If it's an ISO string or other string format
  if (typeof dateValue === "string") {
    try {
      const date = parseISO(dateValue);
      return isValid(date) ? date : null;
    } catch (error) {
      console.warn("Failed to parse date string:", error);
      return null;
    }
  }

  // If it's a number (Unix timestamp)
  if (typeof dateValue === "number") {
    try {
      const date = new Date(dateValue);
      return isValid(date) ? date : null;
    } catch (error) {
      console.warn("Failed to convert timestamp number:", error);
      return null;
    }
  }

  console.warn("Unknown date format:", dateValue);
  return null;
};

/**
 * Formats a date value for display
 * @param {any} dateValue - The date value to format
 * @param {string} formatString - The format string (default: "MMM dd, yyyy")
 * @returns {string} - Formatted date string or fallback text
 */
export const formatDate = (dateValue, formatString = "MMM dd, yyyy") => {
  const date = toDate(dateValue);
  if (!date) return "Invalid date";

  try {
    return format(date, formatString);
  } catch (error) {
    console.warn("Failed to format date:", error);
    return "Invalid date";
  }
};

/**
 * Formats a date value for datetime display
 * @param {any} dateValue - The date value to format
 * @param {string} formatString - The format string (default: "MMM dd, yyyy 'at' h:mm a")
 * @returns {string} - Formatted datetime string or fallback text
 */
export const formatDateTime = (
  dateValue,
  formatString = "MMM dd, yyyy 'at' h:mm a"
) => {
  return formatDate(dateValue, formatString);
};

/**
 * Formats a date value for form inputs (YYYY-MM-DD)
 * @param {any} dateValue - The date value to format
 * @returns {string} - Date string in YYYY-MM-DD format or empty string
 */
export const formatDateForInput = (dateValue) => {
  const date = toDate(dateValue);
  if (!date) return "";

  try {
    return format(date, "yyyy-MM-dd");
  } catch (error) {
    console.warn("Failed to format date for input:", error);
    return "";
  }
};

/**
 * Formats a relative time (e.g., "2 hours ago", "3 days ago")
 * @param {any} dateValue - The date value to format
 * @returns {string} - Relative time string or fallback text
 */
export const formatRelativeTime = (dateValue) => {
  const date = toDate(dateValue);
  if (!date) return "Unknown time";

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  } else {
    return formatDate(date);
  }
};

/**
 * Checks if a date value is valid
 * @param {any} dateValue - The date value to check
 * @returns {boolean} - Whether the date is valid
 */
export const isValidDate = (dateValue) => {
  const date = toDate(dateValue);
  return date !== null;
};

/**
 * Gets the current date as a string formatted for form inputs
 * @returns {string} - Current date in YYYY-MM-DD format
 */
export const getCurrentDateForInput = () => {
  return formatDateForInput(new Date());
};

/**
 * Converts a date to ISO string for API calls
 * @param {any} dateValue - The date value to convert
 * @returns {string|null} - ISO string or null if invalid
 */
export const toISOString = (dateValue) => {
  const date = toDate(dateValue);
  if (!date) return null;

  try {
    return date.toISOString();
  } catch (error) {
    console.warn("Failed to convert to ISO string:", error);
    return null;
  }
};

/**
 * Format date for display with timezone context
 * @param {any} dateValue - The date value to format
 * @param {string} timezone - Practice timezone (for display purposes)
 * @param {string} formatString - The format string
 * @returns {string} - Formatted date with timezone info
 */
export const formatDateWithTimezone = (
  dateValue,
  timezone = null,
  formatString = "MMM dd, yyyy"
) => {
  const formattedDate = formatDate(dateValue, formatString);
  
  if (timezone && formattedDate !== "Invalid date") {
    try {
      // Get timezone abbreviation for display
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'short'
      }).formatToParts(new Date())
        .find(part => part.type === 'timeZoneName')?.value || timezone;
      
      return `${formattedDate} (${timeZoneAbbr})`;
    } catch (error) {
      console.warn("Failed to format date with timezone:", error);
      return formattedDate;
    }
  }
  
  return formattedDate;
};

/**
 * Format time for display with timezone context
 * @param {string} timeString - Time in HH:MM format
 * @param {string} timezone - Practice timezone (for display purposes)
 * @returns {string} - Formatted time with timezone info
 */
export const formatTimeWithTimezone = (timeString, timezone = null) => {
  if (!timeString) return "Invalid time";
  
  if (timezone) {
    try {
      // Get timezone abbreviation for display
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'short'
      }).formatToParts(new Date())
        .find(part => part.type === 'timeZoneName')?.value || timezone;
      
      return `${timeString} ${timeZoneAbbr}`;
    } catch (error) {
      console.warn("Failed to format time with timezone:", error);
      return timeString;
    }
  }
  
  return timeString;
};

/**
 * Get current date formatted for form inputs, with timezone awareness note
 * @param {string} timezone - Practice timezone (for user information)
 * @returns {object} - Object with date string and timezone info
 */
export const getCurrentDateForInputWithTimezone = (timezone = null) => {
  const dateString = getCurrentDateForInput();
  
  return {
    dateString,
    timezone,
    displayNote: timezone ? `All times shown in ${timezone} timezone` : null
  };
};
